import { FirebaseConfig } from '../config/FirebaseConfig';

class RolesAPI {
  static getBaseUrl() {
    // Always use cloud functions URL for roles APIs
    return `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net`;
  }

  // Get auth headers from Firebase Auth
  static async getAuthHeaders() {
    try {
      // Get Firebase auth from global import
      const firebase = require('common/src/config/configureFirebase').firebase;
      const user = firebase.auth.currentUser;

      if (!user) {
        console.error('No current user found');
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      console.log('Auth headers obtained for user:', user.uid);

      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw new Error('Authentication failed: ' + error.message);
    }
  }

  // Get all roles
  static async getRoles() {
    try {
      console.log('🔄 Getting real roles data...');
      const response = await fetch(`${this.getBaseUrl()}/publicGetRoles`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        roles: data.roles || [],
        availablePages: data.availablePages || {}
      };
    } catch (error) {
      console.error('Error fetching roles:', error);
      return { success: false, error: error.message };
    }
  }

  // Create new role
  static async createRole(roleData) {
    try {
      console.log('🔄 Creating role with real data...');
      const response = await fetch(`${this.getBaseUrl()}/publicCreateRole`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating role:', error);
      return { success: false, error: error.message };
    }
  }

  // Update existing role
  static async updateRole(roleId, roleData) {
    try {
      console.log('🔄 Updating role with real data...');
      const response = await fetch(`${this.getBaseUrl()}/publicUpdateRole`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ roleId, ...roleData })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating role:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete role
  static async deleteRole(roleId) {
    try {
      console.log('🔄 Deleting role with real data...');
      const response = await fetch(`${this.getBaseUrl()}/publicDeleteRole`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ roleId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting role:', error);
      return { success: false, error: error.message };
    }
  }

  // Get users with their role information
  static async getUsersWithRoles() {
    try {
      console.log('🔄 Getting real users with roles data...');
      const response = await fetch(`${this.getBaseUrl()}/publicGetUsersWithRoles`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching users with roles:', error);
      return { success: false, error: error.message };
    }
  }

  // Assign role to user
  static async assignUserRole(userId, roleId) {
    try {
      console.log('🔄 Assigning user role with real data...');
      const response = await fetch(`${this.getBaseUrl()}/publicAssignUserRole`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, roleId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error assigning user role:', error);
      return { success: false, error: error.message };
    }
  }

  // Remove role from user
  static async removeUserRole(userId) {
    try {
      console.log('🔄 Removing user role with real data...');
      const response = await fetch(`${this.getBaseUrl()}/publicAssignUserRole`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, roleId: null })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing user role:', error);
      return { success: false, error: error.message };
    }
  }

  // Get role activities/logs
  static async getRoleActivities(limit = 50, offset = 0) {
    try {
      console.log('🔄 Getting real role activities...');
      const url = `${this.getBaseUrl()}/publicGetRoleActivities?limit=${limit}&offset=${offset}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching role activities:', error);
      return { success: false, error: error.message };
    }
  }

  // Get role statistics
  static async getRoleStatistics() {
    try {
      console.log('🔄 Getting real role statistics...');
      const response = await fetch(`${this.getBaseUrl()}/publicGetRoleStatistics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching role statistics:', error);
      return { success: false, error: error.message };
    }
  }

  // Check user permissions
  static async checkUserPermissions(userId = null, page = null) {
    try {
      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);
      if (page) params.append('page', page);

      const url = `${this.getBaseUrl()}/publicCheckUserPermissions${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking user permissions:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user role and permissions
  static async getUserRole(userId = null) {
    try {
      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);

      const url = `${this.getBaseUrl()}/publicGetUserRole${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user role:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper method to format role data for display
  static formatRoleForDisplay(role) {
    if (!role) return null;

    return {
      ...role,
      formattedCreatedDate: new Date(role.createdAt).toLocaleDateString(),
      formattedUpdatedDate: new Date(role.updatedAt).toLocaleDateString(),
      statusText: role.isActive ? 'Active' : 'Inactive',
      permissionCount: role.permissions ? role.permissions.length : 0
    };
  }

  // Helper method to format user data for display
  static formatUserForDisplay(user) {
    if (!user) return null;

    return {
      ...user,
      fullName: `${user.firstName} ${user.lastName}`.trim() || 'No Name',
      formattedCreatedDate: new Date(user.createdAt).toLocaleDateString(),
      formattedLastLogin: user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never',
      statusText: user.approved ? 'Active' : 'Inactive',
      hasCustomRole: Boolean(user.customRole)
    };
  }

  // Helper method to format activity data for display
  static formatActivityForDisplay(activity) {
    if (!activity) return null;

    return {
      ...activity,
      formattedDate: new Date(activity.timestamp).toLocaleString(),
      formattedTime: new Date(activity.timestamp).toLocaleTimeString(),
      userDisplayName: activity.userName || 'Unknown User'
    };
  }

  // Helper method to get permission display name
  static getPermissionDisplayName(permission, availablePages) {
    if (!permission || !availablePages) return permission;

    const page = availablePages[permission];
    return page ? page.name : permission;
  }

  // Helper method to validate role data
  static validateRoleData(roleData) {
    const errors = [];

    if (!roleData.name || roleData.name.trim().length === 0) {
      errors.push('Role name is required');
    }

    if (!roleData.description || roleData.description.trim().length === 0) {
      errors.push('Role description is required');
    }

    if (!roleData.permissions || !Array.isArray(roleData.permissions) || roleData.permissions.length === 0) {
      errors.push('At least one permission is required');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  // Helper method to check if user can perform action based on their role
  static canUserPerformAction(userPermissions, requiredPermission) {
    if (!userPermissions || !Array.isArray(userPermissions)) {
      return false;
    }

    // Admin users have all permissions
    if (userPermissions.includes('admin')) {
      return true;
    }

    // Check if user has specific permission
    return userPermissions.includes(requiredPermission);
  }

  // Helper method to get role color based on status
  static getRoleStatusColor(isActive) {
    return isActive ? '#4CAF50' : '#F44336'; // Green for active, red for inactive
  }

  // Helper method to get user status color
  static getUserStatusColor(isApproved) {
    return isApproved ? '#4CAF50' : '#FF9800'; // Green for active, orange for inactive
  }
}

export default RolesAPI; 